import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ZoomInLine,
  ZoomOutLine,
} from "@dropbox/dig-icons/assets";
import { FileViewer } from "@mirage/mosaics/FileViewer/FileViewer";
import { tagged } from "@mirage/service-logging";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./DocumentPreviewArea.module.css";

import type { SearchResult } from "@mirage/service-dbx-api";

const logger = tagged("document-preview-area");

type DocumentPreviewAreaProps = {
  result: SearchResult;
  zoomLevel: number;
  currentPage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
};

export function DocumentPreviewArea({
  result,
  zoomLevel,
  currentPage,
  onZoomIn,
  onZoomOut,
}: DocumentPreviewAreaProps) {
  const { t } = useTranslation();
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  const [fileViewerIsErrored, setFileViewerIsErrored] = useState(false);

  const previewUrl = result.media?.previewUrl || result.previewUrl;

  // check to see if this is a Dropbox shared link that can be previewed with FileViewer
  const sharedLink = useMemo(() => {
    if (!result.url) return undefined;

    try {
      const url = new URL(result.url);

      const isDropboxDomain =
        url.hostname === "dropbox.com" ||
        url.hostname.startsWith("www.dropbox.com");
      const hasSharedLinkPath = url.pathname.includes("/scl/fi");

      return isDropboxDomain && hasSharedLinkPath ? result.url : undefined;
    } catch (error) {
      logger.error("Invalid URL provided for shared link check:", error);
      return undefined;
    }
  }, [result.url]);

  const handleImageLoad = () => {
    setImageState("loaded");
  };

  const handleImageError = () => {
    setImageState("error");
  };

  const handleFileViewerError = useCallback((error: string) => {
    logger.error("FileViewer error:", error);
    setFileViewerIsErrored(true);
  }, []);

  // checking to see if this is a Dropbox shared link and FileViewer hasn't errored -> use FileViewer
  if (sharedLink && !fileViewerIsErrored) {
    return (
      <div className={styles.previewArea}>
        <div className={styles.previewControls}>
          <Text size="small" className={styles.zoomInfo}>
            {t("page_of_total", { currentPage, totalPages: 3 })}
          </Text>

          <Button
            variant="outline"
            size="medium"
            withIconEnd={<UIIcon src={ChevronDownLine} />}
            className={styles.zoomPercentage}
          >
            {zoomLevel}%
          </Button>

          <div className={styles.zoomButtons}>
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomInLine} />}
              onClick={onZoomIn}
              disabled={zoomLevel >= 200}
            />
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomOutLine} />}
              onClick={onZoomOut}
              disabled={zoomLevel <= 50}
            />
          </div>
        </div>

        <div className={styles.documentPreview}>
          <div className={styles.documentPage}>
            <div
              className={styles.fileViewerZoomWrapper}
              style={
                {
                  "--zoom-scale": zoomLevel / 100,
                } as React.CSSProperties
              }
            >
              <FileViewer
                sharedLinkUrl={sharedLink}
                fileName={result.title}
                reportError={handleFileViewerError}
              >
                <div />
              </FileViewer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // if it's not a Dropbox shared link or FileViewer has errored, fall back to the existing image preview logic
  return (
    <div className={styles.previewArea}>
      <div className={styles.previewControls}>
        <Text size="small" className={styles.zoomInfo}>
          {t("page_of_total", { currentPage, totalPages: 3 })}
        </Text>

        <Button
          variant="outline"
          size="medium"
          withIconEnd={<UIIcon src={ChevronDownLine} />}
          className={styles.zoomPercentage}
        >
          {zoomLevel}%
        </Button>

        <div className={styles.zoomButtons}>
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomInLine} />}
            onClick={onZoomIn}
            disabled={zoomLevel >= 200}
          />
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomOutLine} />}
            onClick={onZoomOut}
            disabled={zoomLevel <= 50}
          />
        </div>
      </div>

      <div className={styles.documentPreview}>
        <div className={styles.documentPage}>
          {previewUrl && imageState !== "error" ? (
            <div className={styles.thumbnailContainer}>
              {imageState === "loading" && (
                <div className={styles.thumbnailSkeleton}>
                  <Text size="small" color="subtle">
                    {t("loading")}
                  </Text>
                </div>
              )}
              <img
                src={previewUrl}
                alt={t("document_preview_alt", { title: result.title })}
                className={classNames(styles.thumbnailImage, {
                  [styles.thumbnailImageLoaded]: imageState === "loaded",
                  [styles.thumbnailImageLoading]: imageState === "loading",
                })}
                style={
                  {
                    "--zoom-scale": zoomLevel / 100,
                  } as React.CSSProperties
                }
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {imageState === "loaded" && (
                <div className={styles.thumbnailCaption}>
                  <Text size="small" color="subtle">
                    {t("document_preview_label")}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.placeholderContent}>
              <Text size="large" color="subtle">
                {t("no_preview_available")}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
